import { Page } from "../enums/Page";
import { html, css } from "../helpers/webComponents";
import { InputFieldService } from "../services/InputfieldService";
const styles: string = css`
    :host {
        --input-bg: #666666; /* Warm wood-like background */
        --button-bg: #780606; /* Rich brown for buttons */
        --button-color: #F8E4C9; /* Soft ivory text */
        --extra-bg: #4D0404; /* Dark brown for depth */
        --extra-text: #E3C59D; /* Aged gold text */
    }

    game-input {
        font-family: "Onesize";
        padding: 10px;
        display: block;
    }
`;
/**
 * Represents the Clock Puzzle component.
 * This component allows users to input a time and validate it against a predefined clock code.
 * It also provides functionality to exit the puzzle and switch pages.
 */
export class ClockPuzzleComponent extends HTMLElement {
    private _inputFieldService: InputFieldService = new InputFieldService();
    private _element: HTMLElement = html`
    <div>
        <game-input placeholder="What time do you want to set it to?" button-text="Set Time"></game-input>
        <style>${styles}</style>
    </div>`;

    /**
     * Called when the component is connected to the DOM.
     * Attaches a shadow DOM and renders the component.
     * @param {string} code - The input code for the clock puzzle.
     * @param {boolean} check - A flag to check the exit status of the puzzle.
     */
    protected connectedCallback(code: string, check: boolean): void {
        this.attachShadow({ mode: "open" });
        this.render(code, check);
    }

    /**
     * Renders the Clock Puzzle component.
     * Clears the shadow DOM, sets up event listeners, and appends the necessary elements.
     * @param {string} code - The input code for the clock puzzle.
     * @param {boolean} check - A flag to check the exit status of the puzzle.
     */
    protected render(code: string, check: boolean): void {
        if (!this.shadowRoot) {
            return;
        }

        // Remove only previous children instead of resetting innerHTML
        while (this.shadowRoot.firstChild) {
            this.shadowRoot.removeChild(this.shadowRoot.firstChild);
        }

        const input: HTMLElement = this._element.querySelector("game-input") as HTMLElement;

        /**
         * Event listener for the "data-submitted" event.
         * Validates the input code and displays the result.
         */
        input.addEventListener("data-submitted", async (e: Event) => {
            const inputValue: CustomEvent = e as CustomEvent;
            code = inputValue.detail as string;

            if (!code) {
                console.error("Empty input! Please enter a value.");
                return;
            }

            await this._inputFieldService.getClockCode(code).then((codeChecker: boolean) => {
                if (codeChecker) {
                    const correct: HTMLElement = document.createElement("div");
                    correct.innerHTML = "Correct!";
                    this.shadowRoot?.append(correct);
                    setTimeout(() => {
                        this.switchpage();
                    }, 2000);
                }
                else {
                    const incorrect: HTMLElement = document.createElement("div");
                    incorrect.innerHTML = "Incorrect!";
                    this.shadowRoot?.append(incorrect);
                }
            });
        });

        this.shadowRoot.innerHTML = "";
        this.shadowRoot.append(this._element);

        const extraContentDiv: HTMLButtonElement = input.shadowRoot?.querySelector("div[data-type='extra-content']") as HTMLButtonElement;

        const exitButton: HTMLButtonElement = document.createElement("button");
        exitButton.id = "exit-puzzle-button";
        exitButton.textContent = "Exit Puzzle";

        /**
         * Event listener for the "click" event on the exit button.
         * Dispatches an event to exit the puzzle and switches the page.
         */
        exitButton.addEventListener("click", (e: Event) => {
            e.preventDefault();
            const exitValue: CustomEvent = new CustomEvent("exit-puzzle", {
                detail: true,
            });

            check = exitValue.detail as boolean;
            if (!check) {
                console.error("Exiting puzzle failed");
                return;
            }
            this.switchpage();
        });

        const exitPuzzle: CustomEvent = new CustomEvent("exit-puzzle", {
            detail: { page: Page.Canvas },
        });
        this.dispatchEvent(exitPuzzle);

        extraContentDiv.appendChild(exitButton);
    }

    /**
     * Switches the page to the game canvas.
     * Dispatches a "game-switch-page" event with the target page details.
     */
    private switchpage(): void {
        const event: CustomEvent = new CustomEvent("game-switch-page", {
            detail: { page: Page.Canvas }, // Page.Canvas refers to the game canvas
            bubbles: true,
            composed: true,
        });
        this.dispatchEvent(event);
    }
}
